import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { DamageCostEvaluationDtoAll } from "@/common/mapping/mappers/damageCostEvaluation";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageCostEvaluationDto,
  DamageCostEvaluationItemDto,
  DamageCostEvaluationItemReferenceDto,
  DamageCostEvaluationReferenceDto,
} from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getContractInlineTitleShort } from "../Contract/ContractInline";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { DamageCostEvaluationViewModal } from "./View/DamageCostEvaluationViewModal";

const defaultDisplayProps = { vehicle: true, contract: true };
type DisplayProps = typeof defaultDisplayProps;

export const getDamageCostEvaluationInlineTitle = (
  data: Nil<DamageCostEvaluationDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          enumService.getEnumValueName(
            ApiEnumName.DamageCostEvaluationStage,
            data.stageHistory?.stage,
          ),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
          displayProps?.contract && data.contract
            ? getContractInlineTitleShort(data.contract)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface DamageCostEvaluationInlineProps extends BaseEntityInlineInheritableProps {
  entity: DamageCostEvaluationDto | DamageCostEvaluationReferenceDto | null | undefined;
  entityId?: string | null;
  item?: DamageCostEvaluationItemDto | DamageCostEvaluationItemReferenceDto;
  itemId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

export default function DamageCostEvaluationInline({
  entity,
  entityId,
  item,
  itemId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: DamageCostEvaluationInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      damageCostEvaluationId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const full = entity as DamageCostEvaluationDto;
  // const ref = entity as DamageCostEvaluationReferenceDto;

  item = item || (itemId ? full?.items?.find((x) => x.id === itemId) : undefined);

  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={request}
        icon={<AppIcon of='damageCostEvaluation' inText />}
        content={
          <>
            {getDamageCostEvaluationInlineTitle(entity, displayPropsComputed)}
            {item && <> (#{item.localNumber})</>}
            {!entity && !item && <>Damage cost evaluation</>}
          </>
        }
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Damage cost evaluation"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <DamageCostEvaluationViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                damageCostEvaluationId: entityIdComputed,
                damageCostEvaluation: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
